import { useTranslation } from 'next-i18next'

import { Breakpoint, useCheckBreakpoint } from '~/utils'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import BgPattern from '~/landing/components/BgPattern'
import GradientContainer, { gradientBorder } from '~/landing/components/GradientContainer'
import { useLandingEntryRoute } from '~/landing/contexts/landingEntryRoute'
import StartAnalytics from '~/analytics/start/startAnalytics'
import Button from '~/components/Buttons/Button'

export default function BaseBuildBurn({
  buttonClassName,
  onClick,
}: {
  buttonClassName?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
}): React.ReactElement {
  const { t } = useTranslation('landing')
  const entryRoute = useLandingEntryRoute()
  const isMobile = useCheckBreakpoint({ lt: Breakpoint.LG })

  const smBaseClass = 'h-24 -mr-32 lg:mr-0 lg:h-17 xl:h-19 2xl:h-23'
  const mdBaseClass = 'h-38 lg:h-17 xl:h-19 xl:pt-0 2xl:pt-0 2xl:h-22'
  const baseTextClassName = 'text-md lg:text-3 lg:!leading-4 lg:mr-0 lg:pr-8 xl:text-xs xl:pr-10 2xl:text-sm'
  const textClass = (isSmall: boolean): string =>
    ` ${baseTextClassName} ${
      isSmall
        ? 'mr-36 max-w-70 lg:max-w-75 xl:max-w-92.5 2xl:max-w-112.5 lg:pl-12 xl:pl-18 2xl:pl-28'
        : 'mr-3 max-w-85 lg:max-w-100 xl:max-w-112.5 2xl:max-w-132 lg:pl-14 xl:pl-12 2xl:pl-18'
    } `

  const GRADIENT_ITEMS = [
    {
      key: 'burn',
      imageSrcKey: 'landing/gradientbox-sm.png',
      isSmallBox: true,
      textClassName: textClass(true),
    },
    {
      key: 'build',
      imageSrcKey: isMobile ? 'landing/gradientbox-lg.png' : 'landing/gradientbox-md-v2.png',
      isSmallBox: false,
      textClassName: textClass(false),
    },
    {
      key: 'base',
      imageSrcKey: 'landing/gradientbox-sm.png',
      showBorder: !isMobile,
      isSmallBox: true,
      textClassName: `${textClass(true)} 2xl:!pl-36 `,
    },
  ]

  const handleButtonClick = (): void => {
    StartAnalytics.logCtaClick('onboarding_cta', 'landing_section_5_cta')
  }

  return (
    <div className="flex flex-col justify-center items-center relative w-full h-full text-white mx-auto overflow-hidden pb-12">
      <BgPattern />
      <ResponsiveImage
        className="absolute min-w-196 w-full max-w-302 -ml-[600px] mt-16"
        height={886}
        imageClassName="object-contain"
        srcKey="landing/pad-pattern.png"
        width={1378}
      />
      <div className="flex flex-col text-center justify-center items-center mt-24 mx-6 md:mx-4 max-w-6xl z-10">
        <h1 className="text-3xl lg:text-5xl font-extrabold">{t('burnBuildBase.theBase')}</h1>
        <p className="text-md px-0 md:px-24 lg:px-56 2xl:px-64 text-blue-pastel mt-6">
          {t('burnBuildBase.researchAndTeaching')}
        </p>
      </div>
      <div className="flex flex-col lg:flex-row relative justify-center items-center lg:items-start w-full mt-2 z-10">
        <ResponsiveImage
          className="relative lg:absolute z-10 lg:pl-24"
          fit="contain"
          height={640}
          imageClassName="md:pl-0 w-12 min-w-105 sm:min-w-184 lg:min-w-150 lg:mt-3 xl:-mt-2 xl:min-w-168 2xl:mt-0 2xl:min-w-196"
          loading="eager"
          srcKey="landing/baseBuildBurn-v2.png"
          width={1200}
        />

        <div className="grid lg:grid-cols-2 w-full h-full pt-19 xl:pt-14 2xl:pt-20">
          <div className="flex flex-col-reverse lg:flex-col w-full h-full justify-start lg:pr-28 xl:pr-30 2xl:pr-40 gap-18 lg:-mt-1 xl:mt-1 2xl:mt-0 lg:gap-3 xl:gap-3 2xl:gap-3">
            {GRADIENT_ITEMS.map(({ key, imageSrcKey, textClassName, showBorder, isSmallBox }) => (
              <GradientContainer
                className={isSmallBox ? smBaseClass : mdBaseClass}
                description={`landing:burnBuildBase.${key}`}
                imageSrcKey={imageSrcKey}
                key={key}
                showBorder={showBorder}
                textClassName={textClassName}
                title={isMobile ? `burnBuildBase.${key}Title` : null}
                titleClassName={isSmallBox ? 'right-70' : 'right-36'}
              />
            ))}
          </div>

          <div className="flex items-start pt-1 bg-gray-100">
            <div className="relative hidden lg:block md:h-12 lg:h-14 xl:h-16 2xl:h-20 w-full">{gradientBorder()}</div>
          </div>
        </div>
      </div>
      <div className="max-w-xl lg:max-w-4xl z-10 text-center text-blue-pastel mt-6">
        <p className="text-md mx-auto px-2">{t('burnBuildBase.inHouseOfMath')}</p>

        <Button
          className={
            buttonClassName ||
            'flex max-w-fit !font-black text-md text-center mx-auto px-8 py-4 !bg-green-lime !text-black border-4 rounded-full hover:scale-110 duration-100 relative z-20 !mt-14'
          }
          href={onClick ? undefined : entryRoute}
          onClick={onClick || handleButtonClick}
        >
          {t('burnBuildBase.startLearningMath')}
        </Button>
      </div>
    </div>
  )
}
